import React from "react";
import { PageHeader } from "@finitless/shared/components";
import { useTranslation } from "react-i18next";
import {
  Icon,
  Switch,
  Skeleton,
  PaymentsHowItWorksModal,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../ui";
import { usePostHog } from "@finitless/shared/hooks";
import { useStripeConnect } from "../../hooks/useStripeConnect";

const OnlinePaymentsSettingsPage = ({ selectedBrand }) => {
  const { t } = useTranslation("settings");
  const { isFeatureEnabled } = usePostHog();
  const showSandboxControl = isFeatureEnabled(
    "stripe_connect_link_sandbox_control",
  );
  const [isPaymentsActive, setIsPaymentsActive] = React.useState(true);
  const [isSandbox, setIsSandbox] = React.useState(false);
  const [isRedirectingToStripe, setIsRedirectingToStripe] =
    React.useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] =
    React.useState(false);
  const didAutoInstallRef = React.useRef(false);

  const {
    useStripeConnectPlugins,
    useInstallStripeConnectPlugin,
    useTogglePluginEnabled,
    usePaymethod,
    useTogglePaymethodEnabled,
    useStatus,
    useOnboardingLink,
    useSandboxMode,
  } = useStripeConnect();

  const {
    data: plugins,
    isLoading: isLoadingPlugins,
    isError: isPluginsError,
    error: pluginsError,
    refetch: refetchPlugins,
  } = useStripeConnectPlugins();

  const { mutate: installStripeConnectPlugin, isPending: isInstallingPlugin } =
    useInstallStripeConnectPlugin();

  const { isPending: isTogglingPlugin } = useTogglePluginEnabled();

  const { mutate: toggleSandboxMode, isPending: isTogglingSandbox } =
    useSandboxMode();

  const hasStripeConnectPlugin = (plugins?.length ?? 0) > 0;
  const isPluginsLoading = isLoadingPlugins;
  const stripeConnectPlugin = plugins?.[0] ?? null;
  const stripeConnectPluginId = stripeConnectPlugin?.id;
  const stripeConnectPluginEnabled = !!stripeConnectPlugin?.enabled;

  React.useEffect(() => {
    if (didAutoInstallRef.current) {
      return;
    }
    if (isPluginsLoading) {
      return;
    }
    if (isPluginsError) {
      return;
    }
    if (hasStripeConnectPlugin) {
      return;
    }
    if (isInstallingPlugin) {
      return;
    }

    didAutoInstallRef.current = true;
    installStripeConnectPlugin({});
  }, [
    hasStripeConnectPlugin,
    installStripeConnectPlugin,
    isInstallingPlugin,
    isPluginsLoading,
    isPluginsError,
  ]);

  const {
    data: status,
    isLoading: isLoadingStatus,
    isFetching: isFetchingStatus,
    refetch: refetchStatus,
  } = useStatus({
    enabled:
      !isPluginsLoading &&
      !isInstallingPlugin &&
      hasStripeConnectPlugin &&
      stripeConnectPluginEnabled &&
      !isTogglingPlugin,
  });

  const { data: paymethod, refetch: refetchPaymethod } = usePaymethod({
    enabled:
      hasStripeConnectPlugin &&
      !isPluginsLoading &&
      !isInstallingPlugin &&
      !isLoadingStatus &&
      !!status,
  });

  const { mutate: togglePaymethodEnabled, isPending: isTogglingPaymethod } =
    useTogglePaymethodEnabled();

  const paymethodId = paymethod?.id;

  React.useEffect(() => {
    if (paymethod?.enabled !== undefined) {
      setIsPaymentsActive(!!paymethod.enabled);
    }
  }, [paymethod?.enabled, paymethod?.id]);

  const { mutate: getOnboardingLink, isPending: isCreatingLink } =
    useOnboardingLink();

  const isRefreshing = isLoadingStatus || isFetchingStatus;

  const handleRefreshStatus = async () => {
    await refetchStatus();
    refetchPaymethod();
  };

  const statusValue = status?.status || "not_started";
  const isConnected = statusValue === "connected";
  const isIncomplete = statusValue === "incomplete";
  const requirements = status?.requirements || [];
  const isConnectedButSetupIncomplete =
    isConnected && (!status?.charges_enabled || !status?.payouts_enabled);
  const isFullyConnected =
    isConnected && !!status?.charges_enabled && !!status?.payouts_enabled;

  React.useEffect(() => {
    if (status?.sandbox !== undefined) {
      setIsSandbox(!!status.sandbox);
    }
  }, [status?.sandbox]);

  const handleSandboxChange = (nextSandbox) => {
    setIsSandbox(nextSandbox);
    toggleSandboxMode(
      { sandbox: nextSandbox },
      {
        onError: () => {
          setIsSandbox(!nextSandbox);
        },
      },
    );
  };

  const handleStartRedirect = () => {
    if (isRedirectingToStripe) {
      return false;
    }
    setIsRedirectingToStripe(true);
    return true;
  };

  const handlePaymentsActiveChange = (nextEnabled) => {
    if (!paymethodId) {
      return;
    }
    if (nextEnabled && !isFullyConnected) {
      return;
    }
    setIsPaymentsActive(nextEnabled);
    togglePaymethodEnabled(
      { paymethodId, enabled: nextEnabled },
      {
        onError: () => {
          setIsPaymentsActive(!nextEnabled);
        },
      },
    );
  };

  const handleConnectStripe = () => {
    const canProceed = handleStartRedirect();
    if (!canProceed) {
      return;
    }

    getOnboardingLink(
      {
        refresh_url: window.location.href,
        return_url: window.location.href,
      },
      {
        onSuccess: (data) => {
          if (data?.url) {
            window.location.href = data.url;
          }
        },
        onError: () => {
          setIsRedirectingToStripe(false);
        },
      },
    );
  };

  const LoadingSkeleton = () => (
    <div className="animate-pulse space-y-8">
      <div className="rounded-2xl border border-gray-800 bg-gray-900 p-8 md:p-10">
        <div className="flex flex-col items-center justify-between gap-10 md:flex-row md:items-start">
          <div className="w-full flex-1 space-y-6">
            <div className="flex items-center gap-4">
              <Skeleton className="h-12 w-12 rounded-xl" />
              <Skeleton className="h-8 w-64" />
            </div>
            <div className="space-y-4">
              <Skeleton className="h-4 w-full max-w-md" />
              <Skeleton className="h-4 w-full max-w-sm" />
              <Skeleton className="h-4 w-full max-w-xs" />
            </div>
            <Skeleton className="h-11 w-40 rounded-xl" />
          </div>
          <Skeleton className="hidden h-64 w-64 rounded-3xl lg:block" />
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-800 bg-gray-900">
        <div className="flex justify-between border-b border-gray-800 p-6">
          <div className="flex gap-4">
            <Skeleton className="h-10 w-10 rounded-xl" />
            <div className="space-y-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
          <Skeleton className="h-6 w-16 rounded-full" />
        </div>
        <div className="flex flex-col gap-12 p-8 md:p-10 lg:flex-row">
          <div className="flex-1 space-y-6">
            <Skeleton className="h-7 w-full max-w-md" />
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
            </div>
            <div className="space-y-4">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-5 w-56" />
            </div>
          </div>
          <Skeleton className="h-64 flex-1 rounded-3xl" />
        </div>
      </div>
    </div>
  );

  const StripeActions = ({
    variant, // "connect" | "primary" | "manage"
    primaryText,
    onPrimaryClick,
    isPrimaryDisabled,
    isPrimaryLoading,
    onRefreshClick,
    isRefreshing: isRefreshingButton,
    onLearnHowItWorks,
  }) => {
    const showRefresh = typeof onRefreshClick === "function";
    const showLearn = typeof onLearnHowItWorks === "function";

    const isConnect = variant === "connect";
    const isManage = variant === "manage";

    const containerClassName = isConnect
      ? "flex flex-wrap items-center gap-6"
      : "flex flex-wrap items-center gap-4";

    const primaryClassName = isConnect
      ? "h-11 px-8 rounded-xl bg-brand-gradient hover:opacity-90 text-white font-bold text-sm transition-all duration-200 shadow-lg shadow-blue-500/25 active:scale-95 disabled:opacity-50 flex items-center gap-2"
      : isManage
        ? "h-10 px-6 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
        : "h-11 px-8 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm transition-all duration-200 shadow-lg shadow-amber-500/25 active:scale-95 disabled:opacity-50 flex items-center gap-2";

    const refreshClassName = isManage
      ? "h-10 px-6 rounded-lg bg-gray-800 hover:bg-gray-700 text-white font-bold text-xs transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
      : "h-11 px-6 rounded-xl bg-gray-800 border border-gray-700 hover:bg-gray-700 text-white font-bold text-sm transition-all duration-200 active:scale-95 disabled:opacity-50 flex items-center gap-2";

    const loaderSize = isManage ? 14 : 16;

    return (
      <div className={containerClassName}>
        <button
          type="button"
          onClick={onPrimaryClick}
          disabled={isPrimaryDisabled}
          className={primaryClassName}
        >
          {isPrimaryLoading ? (
            <Icon name="Loader2" className="animate-spin" size={loaderSize} />
          ) : isManage ? (
            <Icon name="ExternalLink" size={14} />
          ) : null}
          {primaryText}
        </button>

        {showRefresh ? (
          <button
            type="button"
            onClick={onRefreshClick}
            disabled={isRefreshingButton}
            className={refreshClassName}
          >
            <Icon
              name="RefreshCcw"
              className={isRefreshingButton ? "animate-spin" : ""}
              size={14}
            />
            {t("onlinePayments.actions.refreshStatus")}
          </button>
        ) : null}

        {showLearn ? (
          <button
            type="button"
            onClick={onLearnHowItWorks}
            className="flex items-center gap-1 text-xs font-bold text-gray-300 transition-colors hover:text-white"
            aria-label={t("onlinePayments.actions.learnHowItWorks")}
          >
            {t("onlinePayments.actions.learnHowItWorks")}
            <Icon name="ChevronRight" size={14} className="text-gray-400" />
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <div>
      <PageHeader
        title={t("onlinePayments.header.title")}
        subtitle={t("onlinePayments.header.subtitle")}
        selectedBrand={selectedBrand}
      />

      <div className="space-y-6">
        <div className="flex justify-end">
          {isPluginsLoading || isLoadingStatus || isTogglingPlugin ? (
            <div className="h-6 w-24 animate-pulse rounded-full bg-gray-800"></div>
          ) : isIncomplete ? (
            <span className="flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-900/20 px-3 py-1 text-[10px] font-bold tracking-wider text-amber-500 uppercase">
              <Icon name="AlertTriangle" size={12} />
              {t("onlinePayments.badges.actionRequired")}
            </span>
          ) : isConnected && stripeConnectPluginEnabled ? (
            <span className="rounded-full border border-green-500/30 bg-green-900/20 px-3 py-1 text-[10px] font-bold tracking-wider text-green-400 uppercase">
              {t("onlinePayments.badges.connected")}
            </span>
          ) : (
            <span className="rounded-full border border-red-500/30 bg-red-900/20 px-3 py-1 text-[10px] font-bold tracking-wider text-red-400 uppercase">
              {t("onlinePayments.badges.notConnected")}
            </span>
          )}
        </div>

        {isPluginsError ? (
          <div className="rounded-2xl border border-red-500/30 bg-gray-900 p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-red-500/20 bg-red-500/10">
                <Icon name="AlertTriangle" className="text-red-400" size={20} />
              </div>
              <div className="flex-1">
                <p className="font-bold text-white">
                  {t("onlinePayments.error.loadPluginsTitle")}
                </p>
                <p className="mt-1 text-sm text-gray-400">
                  {pluginsError?.message || t("onlinePayments.error.tryAgain")}
                </p>
                <div className="mt-4">
                  <button
                    type="button"
                    aria-label={t("onlinePayments.actions.retryLoadingPlugins")}
                    onClick={() => refetchPlugins()}
                    className="h-10 rounded-lg border border-gray-700 bg-gray-800 px-4 text-xs font-bold text-white transition-all hover:bg-gray-700 active:scale-95"
                  >
                    {t("onlinePayments.actions.retry")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {isPluginsLoading || isInstallingPlugin || !hasStripeConnectPlugin ? (
          <LoadingSkeleton />
        ) : !stripeConnectPluginEnabled ? null : isLoadingStatus ? (
          <LoadingSkeleton />
        ) : statusValue === "not_started" ? (
          <div className="group relative overflow-hidden rounded-2xl border border-gray-800 bg-gray-900 p-8 md:p-10">
            <div className="relative z-10 flex flex-col items-center justify-between gap-10 md:flex-row md:items-start">
              <div className="w-full flex-1 text-left">
                <div className="mb-6 flex items-center gap-4">
                  <div className="bg-brand-gradient flex h-12 w-12 items-center justify-center rounded-xl shadow-lg shadow-blue-500/20">
                    <Icon name="CreditCard" className="text-white" size={24} />
                  </div>
                  <h2 className="text-2xl font-bold tracking-tight text-white">
                    {t("onlinePayments.notStarted.title")}
                  </h2>
                </div>

                <ul className="mb-10 space-y-4">
                  <li className="flex items-center gap-3">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-green-500/20 bg-green-500/10">
                      <Icon name="Check" className="text-green-500" size={14} />
                    </div>
                    <span className="text-[15px] font-medium text-gray-300">
                      {t("onlinePayments.notStarted.bulletSecure", {
                        brand: "Stripe",
                      })}
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-green-500/20 bg-green-500/10">
                      <Icon name="Check" className="text-green-500" size={14} />
                    </div>
                    <span className="text-[15px] font-medium text-gray-300">
                      {t("onlinePayments.notStarted.bulletPayouts")}
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-green-500/20 bg-green-500/10">
                      <Icon name="Check" className="text-green-500" size={14} />
                    </div>
                    <span className="text-[15px] font-medium text-gray-300">
                      {t("onlinePayments.notStarted.bulletSetupTime")}
                    </span>
                  </li>
                </ul>

                <StripeActions
                  variant="connect"
                  primaryText={t("onlinePayments.actions.connectStripe")}
                  onPrimaryClick={handleConnectStripe}
                  isPrimaryDisabled={isCreatingLink || isRedirectingToStripe}
                  isPrimaryLoading={isCreatingLink || isRedirectingToStripe}
                  onLearnHowItWorks={() => setIsHowItWorksModalOpen(true)}
                />
              </div>

              <div className="hidden h-64 w-64 flex-shrink-0 items-center justify-center rounded-3xl border border-gray-800 bg-black/20 shadow-inner lg:flex">
                <div className="relative">
                  <div className="absolute -inset-4 rounded-full bg-blue-500/10 blur-2xl"></div>
                  <Icon
                    name="CreditCard"
                    size={80}
                    className="relative z-10 text-blue-500/30"
                  />
                </div>
              </div>
            </div>
          </div>
        ) : isIncomplete ? (
          <div className="group relative overflow-hidden rounded-2xl border border-gray-800 bg-gray-900 p-8 md:p-10">
            <div className="relative z-10">
              <h2 className="mb-1 text-2xl font-bold tracking-tight text-white">
                {t("onlinePayments.incomplete.title")}
              </h2>
              <p className="mb-8 text-sm text-gray-400 italic">
                {t("onlinePayments.incomplete.subtitle")}
              </p>

              {requirements.length > 0 ? (
                <div className="mb-10">
                  <p className="mb-4 text-[10px] font-black tracking-widest text-gray-500 uppercase">
                    {t("onlinePayments.incomplete.requirementsTitle")}
                  </p>
                  <div className="space-y-3">
                    {requirements.map((req) => (
                      <div
                        key={req.key}
                        className="flex items-center rounded-xl border border-gray-800 bg-gray-800/40 p-4 transition-colors hover:border-amber-500/30"
                      >
                        <div className="flex items-center gap-3">
                          <Icon
                            name="AlertCircle"
                            className="text-amber-500"
                            size={18}
                          />
                          <span className="text-sm font-bold text-gray-200">
                            {req.label}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {requirements.length > 0 ? (
                <StripeActions
                  variant="primary"
                  primaryText={t("onlinePayments.actions.completeSetup")}
                  onPrimaryClick={handleConnectStripe}
                  isPrimaryDisabled={isCreatingLink || isRedirectingToStripe}
                  isPrimaryLoading={isCreatingLink || isRedirectingToStripe}
                  onRefreshClick={handleRefreshStatus}
                  isRefreshing={isRefreshing}
                />
              ) : (
                <StripeActions
                  variant="primary"
                  primaryText={t("onlinePayments.actions.refreshStatus")}
                  onPrimaryClick={handleRefreshStatus}
                  isPrimaryDisabled={isRefreshing}
                  isPrimaryLoading={isRefreshing}
                />
              )}

              <div className="mt-8 flex items-center gap-2 text-amber-500/80">
                <Icon name="Info" size={14} />
                <p className="text-xs font-bold">
                  {t("onlinePayments.incomplete.note")}
                </p>
              </div>
            </div>
          </div>
        ) : isConnectedButSetupIncomplete ? (
          <div className="relative overflow-hidden rounded-2xl border border-gray-800 bg-gray-900 p-8 md:p-10">
            <div className="relative z-10">
              <h2 className="mb-2 text-2xl font-bold tracking-tight text-white">
                {t("onlinePayments.setupInProgress.title")}
              </h2>
              <p className="mb-8 max-w-2xl text-sm text-gray-400">
                {t("onlinePayments.setupInProgress.subtitle")}
              </p>
              <p className="-mt-6 mb-8 max-w-2xl text-xs text-gray-500">
                {t("onlinePayments.setupInProgress.note")}
              </p>

              {requirements.length > 0 ? (
                <StripeActions
                  variant="primary"
                  primaryText={t("onlinePayments.actions.continueInStripe")}
                  onPrimaryClick={handleConnectStripe}
                  isPrimaryDisabled={isCreatingLink || isRedirectingToStripe}
                  isPrimaryLoading={isCreatingLink || isRedirectingToStripe}
                  onRefreshClick={handleRefreshStatus}
                  isRefreshing={isRefreshing}
                />
              ) : (
                <StripeActions
                  variant="primary"
                  primaryText={t("onlinePayments.actions.refreshStatus")}
                  onPrimaryClick={handleRefreshStatus}
                  isPrimaryDisabled={isRefreshing}
                  isPrimaryLoading={isRefreshing}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="group relative overflow-hidden rounded-2xl border border-l-4 border-gray-800 border-l-green-500/50 bg-gray-900 p-8 md:p-10">
            <div className="relative z-10">
              <div className="mb-8 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold tracking-tight text-white">
                    {t("onlinePayments.connected.title")}
                  </h2>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-full border border-green-500/20 bg-green-500/10">
                  <Icon
                    name="CheckCircle2"
                    size={24}
                    className="text-green-500"
                  />
                </div>
              </div>

              <div className="mb-10 grid grid-cols-1 gap-4 md:grid-cols-4">
                <div className="rounded-xl border border-gray-800 bg-gray-800/40 p-4">
                  <p className="mb-1 text-[10px] font-black tracking-widest text-gray-500 uppercase">
                    {t("onlinePayments.connected.accountLabel")}
                  </p>
                  <p className="truncate text-sm font-bold text-gray-200">
                    {status?.email ||
                      t("onlinePayments.connected.accountFallback")}
                  </p>
                </div>
                <div className="rounded-xl border border-gray-800 bg-gray-800/40 p-4">
                  <p className="mb-1 text-[10px] font-black tracking-widest text-gray-500 uppercase">
                    {t("onlinePayments.connected.chargesLabel")}
                  </p>
                  <p
                    className={`text-sm font-bold ${
                      status?.charges_enabled
                        ? "text-green-500"
                        : "text-red-500"
                    }`}
                  >
                    {status?.charges_enabled
                      ? t("onlinePayments.connected.enabled")
                      : t("onlinePayments.connected.disabled")}
                  </p>
                </div>
                <div className="rounded-xl border border-gray-800 bg-gray-800/40 p-4">
                  <p className="mb-1 text-[10px] font-black tracking-widest text-gray-500 uppercase">
                    {t("onlinePayments.connected.payoutsLabel")}
                  </p>
                  <p
                    className={`text-sm font-bold ${
                      status?.payouts_enabled
                        ? "text-green-500"
                        : "text-red-500"
                    }`}
                  >
                    {status?.payouts_enabled
                      ? t("onlinePayments.connected.enabled")
                      : t("onlinePayments.connected.disabled")}
                  </p>
                </div>
                <div className="rounded-xl border border-gray-800 bg-gray-800/40 p-4">
                  <p className="mb-1 text-[10px] font-black tracking-widest text-gray-500 uppercase">
                    {t("onlinePayments.connected.currencyLabel")}
                  </p>
                  <p className="text-sm font-bold text-gray-200 uppercase">
                    {status?.currency || "USD"}
                  </p>
                </div>
              </div>

              <StripeActions
                variant="manage"
                primaryText={t("onlinePayments.actions.manageInStripe")}
                onPrimaryClick={handleConnectStripe}
                isPrimaryDisabled={isCreatingLink || isRedirectingToStripe}
                isPrimaryLoading={isCreatingLink || isRedirectingToStripe}
                onRefreshClick={handleRefreshStatus}
                isRefreshing={isRefreshing}
                onLearnHowItWorks={() => setIsHowItWorksModalOpen(true)}
              />
            </div>
          </div>
        )}

        {!isPluginsLoading &&
        !isInstallingPlugin &&
        hasStripeConnectPlugin &&
        !isLoadingStatus ? (
          <div className="overflow-hidden rounded-2xl border border-gray-800 bg-gray-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-800 bg-gray-800/20 p-6">
              <div className="flex items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-700 bg-gray-800 shadow-inner">
                  <Icon name="Bot" className="text-white" size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-bold tracking-tight text-white">
                    {t("onlinePayments.panel.title")}
                  </h3>
                  <p className="text-[10px] font-bold tracking-widest text-gray-500 uppercase">
                    {t("onlinePayments.panel.subtitle")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-6">
                {showSandboxControl && (
                  <div className="flex items-center gap-3">
                    <span
                      className={`text-[10px] font-black tracking-widest uppercase transition-colors ${
                        isSandbox ? "text-amber-400" : "text-gray-500"
                      }`}
                    >
                      {t("onlinePayments.panel.sandbox")}
                    </span>
                    <Switch
                      checked={isSandbox}
                      onCheckedChange={handleSandboxChange}
                      className="data-[state=checked]:bg-amber-500"
                      disabled={!stripeConnectPluginId || isTogglingSandbox}
                      aria-label={t("onlinePayments.actions.toggleSandbox")}
                    />
                  </div>
                )}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-3">
                        <span
                          className={`text-[10px] font-black tracking-widest uppercase transition-colors ${
                            isPaymentsActive
                              ? "text-green-400"
                              : "text-gray-500"
                          }`}
                        >
                          {isPaymentsActive
                            ? t("onlinePayments.panel.statusActive")
                            : t("onlinePayments.panel.statusInactive")}
                        </span>
                        <Switch
                          checked={isPaymentsActive}
                          onCheckedChange={handlePaymentsActiveChange}
                          className="data-[state=checked]:bg-green-600"
                          disabled={
                            !paymethodId ||
                            isTogglingPaymethod ||
                            (!isPaymentsActive && !isFullyConnected)
                          }
                          aria-label={t(
                            "onlinePayments.actions.toggleOnlinePayments",
                          )}
                        />
                      </div>
                    </TooltipTrigger>
                    {!isPaymentsActive && !isFullyConnected && (
                      <TooltipContent>
                        {t("onlinePayments.panel.activateDisabledReason")}
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            <div
              className={`flex flex-col gap-12 p-8 transition-opacity duration-300 md:p-10 lg:flex-row ${
                isPaymentsActive
                  ? "opacity-100"
                  : "pointer-events-none opacity-40"
              }`}
            >
              <div className="flex-1">
                <h4 className="mb-6 text-xl leading-tight font-bold text-white">
                  {t("onlinePayments.panel.heroTitle")}
                </h4>
                <div className="mb-10 space-y-4 text-sm leading-relaxed text-gray-400">
                  <p>{t("onlinePayments.panel.heroDescriptionLine1")}</p>
                  <p>{t("onlinePayments.panel.heroDescriptionLine2")}</p>
                </div>

                <ul className="space-y-4">
                  <li className="flex items-center gap-3">
                    <div className="flex h-5 w-5 items-center justify-center rounded-full border border-green-500/20 bg-green-500/10">
                      <Icon name="Check" className="text-green-500" size={12} />
                    </div>
                    <span className="text-sm font-semibold text-white">
                      {t("onlinePayments.panel.bulletDynamicLinks")}
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="flex h-5 w-5 items-center justify-center rounded-full border border-green-500/20 bg-green-500/10">
                      <Icon name="Check" className="text-green-500" size={12} />
                    </div>
                    <span className="text-sm font-semibold text-white">
                      {t("onlinePayments.panel.bulletOrderConfirmation")}
                    </span>
                  </li>
                </ul>
              </div>

              <div className="relative flex-1 rounded-3xl border border-gray-800 bg-black/20 p-8 shadow-2xl">
                <div className="mb-8 flex items-center gap-2 opacity-60">
                  <Icon
                    name="MessageSquare"
                    size={14}
                    className="text-gray-400"
                  />
                  <span className="text-[10px] font-black tracking-widest text-gray-400 uppercase">
                    {t("onlinePayments.preview.title")}
                  </span>
                </div>

                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="bg-brand-gradient flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full shadow-lg shadow-blue-500/30">
                      <Icon name="Bot" className="text-white" size={16} />
                    </div>
                    <div className="max-w-[90%] rounded-2xl rounded-tl-none border border-gray-700 bg-gray-800 p-4 shadow-lg">
                      <p className="text-sm font-medium text-gray-200">
                        {t("onlinePayments.preview.chatLineBefore")}{" "}
                        <span className="text-blue-400">
                          {t("onlinePayments.preview.sampleProduct")}
                        </span>
                        {t("onlinePayments.preview.chatLineAfter")}
                      </p>
                    </div>
                  </div>

                  <div className="relative ml-[52px] overflow-hidden rounded-2xl border border-blue-500/30 bg-gray-800 p-6 shadow-2xl shadow-blue-500/5">
                    <div className="relative z-10">
                      <div className="mb-6 flex items-center justify-between">
                        <h5 className="text-base font-bold tracking-tight text-white">
                          {t("onlinePayments.preview.subtotal")}
                        </h5>
                      </div>
                      <button
                        type="button"
                        className="bg-brand-gradient mb-4 h-11 w-full rounded-xl text-sm font-bold text-white shadow-lg shadow-blue-500/20 transition-all hover:opacity-90 active:scale-95"
                        aria-label={t("onlinePayments.preview.payButtonAria")}
                      >
                        {t("onlinePayments.preview.payButton")}
                      </button>
                      <p className="text-center text-[11px] font-medium text-gray-400 opacity-60">
                        {t("onlinePayments.preview.payNote")}
                      </p>
                    </div>
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-blue-500/5 blur-2xl"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <PaymentsHowItWorksModal
        isOpen={isHowItWorksModalOpen}
        onClose={() => setIsHowItWorksModalOpen(false)}
      />
    </div>
  );
};

export default OnlinePaymentsSettingsPage;
